import { useState, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { CloudArrowUp, X, VideoCamera, Image as ImageIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function UploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  if (!user) return <Navigate to="/auth" replace />;

  const handleFile = (f) => {
    if (!f) return;
    const allowed = ['video/mp4', 'video/webm', 'video/quicktime', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(f.type)) {
      toast.error('Unsupported file type. Use MP4, WebM, JPEG, PNG, GIF, or WebP.');
      return;
    }
    if (f.size > 100 * 1024 * 1024) {
      toast.error('File too large. Max 100MB.');
      return;
    }
    setFile(f);
    if (f.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(f));
    } else {
      setPreview('video');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Select a file first'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('caption', caption);
      formData.append('tags', tags);
      await api.post('/posts', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Posted successfully!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background" data-testid="upload-page">
      <div className="max-w-lg mx-auto px-6 py-10">
        <h1 className="font-serif text-3xl sm:text-4xl text-foreground font-medium tracking-tight" data-testid="upload-title">
          Post a Video
        </h1>
        <p className="text-muted-foreground text-sm mt-2 font-sans">Upload a video or image to the feed</p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {/* Drop zone */}
          {!file ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-sm p-12 text-center cursor-pointer transition-colors duration-300 ${
                dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
              data-testid="upload-dropzone"
            >
              <CloudArrowUp size={48} weight="thin" className="mx-auto text-muted-foreground" />
              <p className="mt-4 text-sm text-muted-foreground font-sans">
                Drag & drop or <span className="text-primary font-semibold">browse</span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground/60 font-sans">MP4, WebM, JPEG, PNG, GIF, WebP (max 100MB)</p>
              <input
                ref={fileRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime,image/jpeg,image/png,image/gif,image/webp"
                onChange={(e) => handleFile(e.target.files[0])}
                className="hidden"
                data-testid="upload-file-input"
              />
            </div>
          ) : (
            <div className="relative border border-border rounded-sm overflow-hidden" data-testid="upload-preview">
              {preview === 'video' ? (
                <div className="h-48 bg-foreground/5 flex items-center justify-center">
                  <div className="text-center">
                    <VideoCamera size={40} weight="thin" className="mx-auto text-primary" />
                    <p className="text-sm text-muted-foreground mt-2 font-sans">{file.name}</p>
                    <p className="text-xs text-muted-foreground/60 font-sans">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                  </div>
                </div>
              ) : (
                <img src={preview} alt="Preview" className="w-full h-48 object-cover" />
              )}
              <button
                type="button"
                onClick={clearFile}
                className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                data-testid="upload-clear-btn"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          )}

          {/* Caption */}
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Caption</Label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Describe your shot..."
              rows={3}
              maxLength={500}
              className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 resize-none px-0 py-2 text-sm font-sans placeholder:text-muted-foreground/50"
              data-testid="upload-caption-input"
            />
            <span className="text-xs text-muted-foreground/50 font-sans">{caption.length}/500</span>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label className="text-xs tracking-wide uppercase text-muted-foreground font-sans font-semibold">Tags</Label>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="driver, swing, hole-in-one"
              className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans placeholder:text-muted-foreground/50"
              data-testid="upload-tags-input"
            />
            <p className="text-xs text-muted-foreground/50 font-sans">Separate with commas</p>
          </div>

          <Button
            type="submit"
            disabled={!file || uploading}
            className="w-full h-12 rounded-sm bg-primary hover:bg-[#083313] transition-colors duration-300 text-sm tracking-wide uppercase font-semibold font-sans disabled:opacity-40"
            data-testid="upload-submit-btn"
          >
            {uploading ? 'Uploading...' : 'Post to Feed'}
          </Button>
        </form>
      </div>
    </div>
  );
}
